import { signal } from "@preact/signals";
import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { auth } from "./db.ts";
import { Button, RelatedServices } from "./uiComponents.tsx";
import { logError } from "./analytics.ts";

const loginEmail = signal("");
const emailSent = signal(false);

const inputStyle = {
  height: 44,
  fontSize: 16,
  padding: "0 12px",
  border: "1.5px solid var(--border)",
  borderRadius: "var(--radius) 0 0 var(--radius)",
  boxSizing: "border-box",
  outline: "none",
  background: "var(--surface-alt)",
  color: "var(--text)",
  marginBottom: 0,
  flex: 1,
};

const buttonStyle = {
  height: 44,
  fontSize: 16,
  borderRadius: "0 var(--radius) var(--radius) 0",
  marginLeft: -1.5, // overlap border
  padding: "0 28px",
  boxSizing: "border-box",
  display: "block",
  border: "1.5px solid var(--border)",
  borderLeft: "none",
  background: "var(--primary)",
  color: "var(--text)",
  fontWeight: 600,
  cursor: "pointer",
};

const Email = () => (
  <div style={{ display: "flex", alignItems: "stretch" }}>
    <input
      style={inputStyle}
      placeholder="Enter email"
      type="email"
      value={loginEmail.value}
      onInput={({ currentTarget: { value } }) => {
        loginEmail.value = value;
      }}
    />
    <Button
      style={buttonStyle}
      onClick={() => {
        if (!loginEmail.value) {
          toast("Fill in your email!");
          return;
        }
        auth
          .sendMagicCode({ email: loginEmail.value })
          .then(() => {
            emailSent.value = true;
          })
          .catch((err) => {
            toast(err.body?.message);
            logError(err);
            loginEmail.value = "";
          });
      }}
    >
      Sign up
    </Button>
  </div>
);

const MagicCode = () => {
  const [code, setCode] = useState("");
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 5, padding: 5 }}
    >
      <div style={{ textAlign: "center" }}>
        Okay, we sent you an email!<br />What was the code?
      </div>
      <div style={{ display: "flex", alignItems: "stretch" }}>
        <input
          type="number"
          placeholder="123456..."
          value={code}
          onInput={(e) => setCode(e.currentTarget.value)}
          style={{
            height: 44,
            fontSize: 16,
            padding: "0 12px",
            border: "1.5px solid var(--border)",
            borderRadius: "var(--radius) 0 0 var(--radius)",
            boxSizing: "border-box",
            outline: "none",
            background: "var(--surface-alt)",
            color: "var(--text)",
            marginBottom: 0,
            flex: 1,
          }}
        />
        <Button
          onClick={() => {
            auth
              .signInWithMagicCode({ email: loginEmail.value, code })
              .then(() => {
                emailSent.value = false;
                toast("Signed in!");
              })
              .catch((err) => {
                toast(`Wrong code 😢`);
                logError(err);
                setCode("");
              });
          }}
          style={{
            height: 44,
            fontSize: 16,
            borderRadius: "0 var(--radius) var(--radius) 0",
            marginLeft: -1.5, // overlap border
            padding: "0 28px",
            boxSizing: "border-box",
            display: "block",
            border: "1.5px solid var(--border)",
            borderLeft: "none",
            background: "var(--primary)",
            color: "var(--text)",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Verify
        </Button>
      </div>
      <div style={{ fontSize: "0.8em" }}>
        if you don't see it, check your spam folder
      </div>
    </div>
  );
};

const LogoTitle = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 16,
      textAlign: "center",
    }}
  >
    <h1
      style={{
        margin: 0,
        fontSize: "2.8em",
        color: "var(--text)",
        fontWeight: 700,
      }}
    >
      🚀 Supergreen
    </h1>
    <p
      style={{
        color: "var(--text-muted)",
        fontSize: "1.1em",
        margin: 0,
        maxWidth: 400,
      }}
    >
      Connect your WhatsApp & Telegram accounts with a simple, powerful API
    </p>
    <p
      style={{
        color: "var(--text-muted)",
        fontSize: "0.95em",
        margin: 0,
        maxWidth: 400,
        fontStyle: "italic",
      }}
    >
      Automate conversations, scale your business, and reach customers where
      they already are
    </p>
  </div>
);

export const AuthScreen = () => {
  const [loginMethod, setLoginMethod] = useState<null | "email">(
    null,
  );
  return (
    <div
      style={{
        display: "flex",
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        padding: "20px",
        gap: 0,
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", flexGrow: 1 }}></div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 40,
          marginBottom: 0,
          maxWidth: 600,
          width: "100%",
        }}
      >
        <LogoTitle />
        {loginMethod === null && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              width: "100%",
              maxWidth: 400,
            }}
          >
            <Button
              style={{
                ...buttonStyle,
                width: "100%",
                borderRadius: "var(--radius)",
                marginLeft: 0,
                borderLeft: "1.5px solid var(--border)",
                background: "var(--primary)",
                color: "var(--text)",
                border: "1.5px solid var(--border)",
              }}
              className="secondary"
              onClick={() => setLoginMethod("email")}
            >
              Get Started with Email
            </Button>
          </div>
        )}
        {loginMethod === "email" &&
          (emailSent.value ? <MagicCode /> : <Email />)}
      </div>
      <div style={{ display: "flex", flexGrow: 1 }}></div>
      <RelatedServices />
      <div style={{ padding: "20px 0", textAlign: "center", width: "100%" }}>
        <a
          href="/legal"
          style={{
            color: "var(--primary)",
            textDecoration: "underline",
            fontSize: 13,
            opacity: 0.8,
          }}
        >
          Terms & Privacy
        </a>
      </div>
    </div>
  );
};

// Alias export to match expected component name
export { AuthScreen as Login };
